import Attribute from "../mechanics/Attribute";
import StatBoard from "../mechanics/StatBoard";

import { WieldMode } from "../mechanics/WieldMode";
import { CharacterData } from "./CharacterData";
import type { CharacterDataJSON } from "./CharacterDataJSON";
import { Vitality } from "../mechanics/Vitality";
import * as Attributes from "../../game/mechanics/Attributes";
import VitalityRatingUtils from "../mechanics/VitalityRating";
import {StatFeatureEnum} from "../mechanics/StatTracking";
import { DiceUtils } from "../mechanics/Dice";

const PointSpendValue = { key: "PointSpendValue", tip: "Base Score" };
const ProfAdjustValue = { key: "ProfAdjustValue", tip: "Proficiency Bonus" };
const StrAdjustValue = { key: "StrAdjustValue", tip: "STR Bonus" };
const AgiAdjustValue = { key: "AgiAdjustValue", tip: "AGI Bonus" };
const IntAdjustValue = { key: "IntAdjustValue", tip: "INT Bonus" };
const ForAdjustValue = { key: "ForAdjustValue", tip: "FOR Bonus" };
const ChaAdjustValue = { key: "ChaAdjustValue", tip: "CHA Bonus" };
const ForChaAdjustValue = { key: "ForChaAdjustValue", tip: "FOR/CHA Bonus" };
const IntChaAdjustValue = { key: "IntChaAdjustValue", tip: "INT/CHA Bonus" };
const AgiIntAdjustValue = { key: "AgiIntAdjustValue", tip: "AGI/INT Bonus" };
const StrForAdjustValue = { key: "StrForAdjustValue", tip: "STR/FOR Bonus" };
const StrAgiAdjustValue = { key: "StrAgiAdjustValue", tip: "STR/AGI Bonus" };
const IntAgiAdjustValue = { key: "IntAgiAdjustValue", tip: "INT/AGI Bonus" };

const lhWeaponAdjustValue = "LHWeapon";
const rhWeaponAdjustValue = "RHWeapon";

export class CharacterDataParser {
    // Helper Functions
    static isLHActive = (characterData: CharacterData) => characterData.wieldMode === WieldMode.DUAL_WIELD 
            || characterData.wieldMode === WieldMode.LEFT_HAND_WIELD 
            || (characterData.wieldMode === WieldMode.TWO_HAND_WIELD && characterData.lh != null);

    static isRHActive = (characterData: CharacterData) => characterData.wieldMode === WieldMode.DUAL_WIELD 
        || characterData.wieldMode === WieldMode.RIGHT_HAND_WIELD 
        || (characterData.wieldMode === WieldMode.TWO_HAND_WIELD && characterData.rh != null);

    static isDualWielding = (characterData: CharacterData) => CharacterDataParser.isLHActive(characterData) && CharacterDataParser.isRHActive(characterData);

    static parseRawCharacterDataToStatBoard(
        charaData: CharacterDataJSON,
        statBoard: StatBoard
    ): void {
        const addPointSpendValue = (statKey: string, value: number) => {
            statBoard.addFeature(
                statKey,
                PointSpendValue.key,
                null,
                value,
                PointSpendValue.tip
            );
        };

        const addModifierValue = (modifierStatKey: string, statKey: string) => {
            statBoard.addFeature(
                modifierStatKey,
                PointSpendValue.key,
                null,
                {
                    type: StatFeatureEnum.ADDEND,
                    value: (): number => {
                        return Math.floor(
                            Number.parseInt(
                                statBoard.getValueByStatKey(statKey, null, [
                                    StatFeatureEnum.ADDEND,
                                    StatFeatureEnum.MODIFIER,
                                ])
                            ) / 2
                        );
                    },
                },
                PointSpendValue.tip
            );
        };

        const addAttributeValue = (
            modifierStatKey: string,
            statKeys: Array<string>,
            attribute: Attribute,
            source?: string,
            tip?: string
        ) => {
            statBoard.addFeature(
                modifierStatKey,
                source ?? ProfAdjustValue.key,
                null,
                {
                    type: StatFeatureEnum.ADDEND,
                    value: (): number => {
                        const statValues = statKeys.map((key) =>
                            Number.parseInt(
                                statBoard.getValueByStatKey(key, null, [
                                    StatFeatureEnum.ADDEND,
                                    StatFeatureEnum.MODIFIER,
                                ])
                            )
                        );
                        const score =
                            statValues.length > 1
                                ? Math.floor(
                                      statValues.reduce(
                                          (acc, curr) => acc + curr,
                                          0
                                      ) / statValues.length
                                  )
                                : statValues.length === 1
                                ? statValues[0]
                                : 0;

                        return attribute.getValueForScore(score);
                    },
                },
                tip ?? ProfAdjustValue.tip
            );
        };

        // Proficiencies
        addPointSpendValue("STR", charaData.strength ?? 0);
        addPointSpendValue("AGI", charaData.agility ?? 0);
        addPointSpendValue("INT", charaData.intelligence ?? 0);
        addPointSpendValue("FOR", charaData.fortitude ?? 0);
        addPointSpendValue("CHA", charaData.charisma ?? 0);

        // Modifiers
        addModifierValue("STRMOD", "STR");
        addModifierValue("AGIMOD", "AGI");
        addModifierValue("INTMOD", "INT");
        addModifierValue("FORMOD", "FOR");
        addModifierValue("CHAMOD", "CHA");

        // Attributes
        addAttributeValue(
            "AWA",
            ["INT"],
            Attributes.Awareness,
            IntAdjustValue.key,
            IntAdjustValue.tip
        );
        addAttributeValue(
            "WGTALL",
            ["STR"],
            Attributes.WeightAllowance,
            StrAdjustValue.key,
            StrAdjustValue.tip
        );
        addAttributeValue(
            "REG",
            ["FOR", "CHA"],
            Attributes.Regen,
            ForChaAdjustValue.key,
            ForChaAdjustValue.tip
        );
        addAttributeValue(
            "SUR",
            ["FOR"],
            Attributes.Survival,
            ForAdjustValue.key,
            ForAdjustValue.tip
        );
        addAttributeValue(
            "WIL",
            ["INT", "CHA"],
            Attributes.Willpower,
            IntChaAdjustValue.key,
            IntChaAdjustValue.tip
        );
        addAttributeValue(
            "STLTH",
            ["AGI", "INT"],
            Attributes.Stealth,
            AgiIntAdjustValue.key,
            AgiIntAdjustValue.tip
        );

        // Vitality
    }

    static parseCharacterDataToStatBoard(
        charaData: CharacterData,
        statBoard: StatBoard
    ): void {
        statBoard.removeFeaturesBySource(["Race"]);
        const addPointSpendValue = (statKey: string, value: number) => {
            statBoard.addFeature(
                statKey,
                PointSpendValue.key,
                null,
                value,
                PointSpendValue.tip
            );
        };

        const addModifierValue = (modifierStatKey: string, statKey: string) => {
            statBoard.addFeature(
                modifierStatKey,
                PointSpendValue.key,
                null,
                {
                    type: StatFeatureEnum.ADDEND,
                    value: (): number => {
                        return Math.floor(
                            Number.parseInt(
                                statBoard.getValueByStatKey(statKey, null, [
                                    StatFeatureEnum.ADDEND,
                                    StatFeatureEnum.MODIFIER,
                                ])
                            ) / 2
                        );
                    },
                },
                PointSpendValue.tip
            );
        };

        const addAttributeValue = (
            attribute: Attribute,
            statKeys: Array<string>,
            source?: string,
            tip?: string
        ) => {
            statBoard.addFeature(
                attribute.statKey,
                source ?? ProfAdjustValue.key,
                null,
                {
                    type: StatFeatureEnum.ADDEND,
                    value: (): number => {
                        const statValues = statKeys.map((key) =>
                            Number.parseInt(
                                statBoard.getValueByStatKey(key, null, [
                                    StatFeatureEnum.ADDEND,
                                    StatFeatureEnum.MODIFIER,
                                ])
                            )
                        );
                        const score =
                            statValues.length > 1
                                ? Math.floor(
                                      statValues.reduce(
                                          (acc, curr) => acc + curr,
                                          0
                                      ) / statValues.length
                                  )
                                : statValues.length === 1
                                ? statValues[0]
                                : 0;

                        return attribute.getValueForScore(score);
                    },
                },
                tip ?? ProfAdjustValue.tip
            );
        };

        // Proficiencies
        addPointSpendValue("STR", charaData.strength);
        addPointSpendValue("AGI", charaData.agility);
        addPointSpendValue("INT", charaData.intelligence);
        addPointSpendValue("FOR", charaData.fortitude);
        addPointSpendValue("CHA", charaData.charisma);

        // Modifiers
        addModifierValue("STRMOD", "STR");
        addModifierValue("AGIMOD", "AGI");
        addModifierValue("INTMOD", "INT");
        addModifierValue("FORMOD", "FOR");
        addModifierValue("CHAMOD", "CHA");

        // Attributes
        addAttributeValue(
            Attributes.Awareness,
            ["INT"],
            IntAdjustValue.key,
            IntAdjustValue.tip
        );
        addAttributeValue(
            Attributes.WeightAllowance,
            ["STR"],
            StrAdjustValue.key,
            StrAdjustValue.tip
        );
        addAttributeValue(
            Attributes.Regen,
            ["FOR", "CHA"],
            ForChaAdjustValue.key,
            ForChaAdjustValue.tip
        );
        addAttributeValue(
            Attributes.Survival,
            ["FOR"],
            ForAdjustValue.key,
            ForAdjustValue.tip
        );
        addAttributeValue(
            Attributes.Willpower,
            ["INT", "CHA"],
            IntChaAdjustValue.key,
            IntChaAdjustValue.tip
        );
        addAttributeValue(
            Attributes.Stealth,
            ["AGI", "INT"],
            AgiIntAdjustValue.key,
            AgiIntAdjustValue.tip
        );

        // Combat Stats
        statBoard.addFeature(
            "DEF",
            StrForAdjustValue.key,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return Attributes.Dodge.getValueForScore(
                        Math.floor(
                            (charaData.strength + charaData.fortitude) / 2
                        )
                    );
                },
            },
            StrForAdjustValue.tip
        );

        statBoard.addFeature(
            "MDEF",
            IntChaAdjustValue.key,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return Attributes.BaseMagicDefense.getValueForScore(
                        Math.floor(
                            (charaData.intelligence + charaData.charisma) / 2
                        )
                    );
                },
            },
            IntChaAdjustValue.tip
        );

        statBoard.addFeature(
            "DDG",
            AgiIntAdjustValue.key,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return Attributes.Dodge.getValueForScore(charaData.agility);
                },
            },
            AgiAdjustValue.tip
        );

        statBoard.addFeature(
            "POW",
            StrAgiAdjustValue.key,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return Attributes.Power.getValueForScore(
                        Math.floor((charaData.strength + charaData.agility) / 2)
                    );
                },
            },
            StrAgiAdjustValue.tip
        );

        statBoard.addFeature(
            "MPOW",
            IntAgiAdjustValue.key,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return Attributes.MagicPower.getValueForScore(
                        Math.floor(
                            (charaData.intelligence + charaData.agility) / 2
                        )
                    );
                },
            },
            IntAgiAdjustValue.tip
        );

        statBoard.addFeature(
            "SPD",
            AgiIntAdjustValue.key,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return Attributes.Speed.getValueForScore(charaData.agility);
                },
            },
            AgiAdjustValue.tip
        );

        statBoard.addFeature(
            "ACC",
            AgiIntAdjustValue.key,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return Attributes.Accuracy.getValueForScore(
                        charaData.agility
                    );
                },
            },
            AgiAdjustValue.tip
        );

        statBoard.addFeature(
            "MACC",
            AgiIntAdjustValue.key,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return Attributes.Accuracy.getValueForScore(
                        charaData.agility
                    );
                },
            },
            AgiAdjustValue.tip
        );

        if(charaData.lh != null) {
            const lhWeaponType = charaData.lh.subTypeString;
            const lhTalentNameMatch = `${lhWeaponType.toLocaleLowerCase().trim()} mastery`;
            const lhWeaponTalent = charaData.talents.find(t => t.title.toLocaleLowerCase().trim() === lhTalentNameMatch);

            if(lhWeaponTalent != null) {
                statBoard.addFeature("ACC", lhWeaponAdjustValue, ["lh"], {type: StatFeatureEnum.ADDEND, value: lhWeaponTalent.rank}, lhWeaponTalent.title);
                statBoard.addFeature("MACC", lhWeaponAdjustValue, ["lh"], {type: StatFeatureEnum.ADDEND, value: lhWeaponTalent.rank}, lhWeaponTalent.title);
            }
        }

        if(charaData.rh != null) {
            const rhWeaponType = charaData.rh.subTypeString;
            const rhTalentNameMatch = `${rhWeaponType.toLocaleLowerCase().trim()} mastery`;
            const rhWeaponTalent = charaData.talents.find(t => t.title.toLocaleLowerCase().trim() === rhTalentNameMatch);

            if(rhWeaponTalent != null) {
                statBoard.addFeature("ACC", rhWeaponAdjustValue, ["rh"], {type: StatFeatureEnum.ADDEND, value: rhWeaponTalent.rank}, rhWeaponTalent.title);
                statBoard.addFeature("MACC", rhWeaponAdjustValue, ["rh"], {type: StatFeatureEnum.ADDEND, value: rhWeaponTalent.rank}, rhWeaponTalent.title);
            }
        }

        // Weapon Adjustments
        // Base Dmg/MDmg
        statBoard.addFeature("DMG", "LHBaseDamage", ["lh"], {type: StatFeatureEnum.ADDEND, value: () => {
            const isLHActive = CharacterDataParser.isLHActive(charaData);
            const isDualWielding = CharacterDataParser.isDualWielding(charaData);

            if(!isLHActive || isDualWielding) {
                return 0;
            }

            const lhProficiencies = charaData.lh?.modifyingProficiencies ?? ["STR", "AGI", "INT"];

            const lhProfScore = isLHActive && !isDualWielding ? Math.max(
                (lhProficiencies.includes("STR") ? charaData.strength : 0),
                (lhProficiencies.includes("AGI") ? charaData.agility : 0),
                (lhProficiencies.includes("INT") ? charaData.intelligence : 0),
                (lhProficiencies.includes("FOR") ? charaData.fortitude : 0),
                (lhProficiencies.includes("CHA") ? charaData.charisma : 0),
            ) : 0;
            
            return Attributes.BaseDamage.getValueForScore(lhProfScore);
        }}, 
        "Base Damage");

        statBoard.addFeature("MDMG", "LHBaseMagicDamage", ["lh"], {type: StatFeatureEnum.ADDEND, value: () => {
            const isLHActive = CharacterDataParser.isLHActive(charaData);
            const isDualWielding = CharacterDataParser.isDualWielding(charaData);

            if(!isLHActive || isDualWielding) {
                return 0;
            }

            const lhProficiencies = charaData.lh?.modifyingProficiencies ?? ["STR", "AGI", "INT"];

            const lhProfScore = isLHActive && !isDualWielding ? Math.max(
                (lhProficiencies.includes("STR") ? charaData.strength : 0),
                (lhProficiencies.includes("AGI") ? charaData.agility : 0),
                (lhProficiencies.includes("INT") ? charaData.intelligence : 0),
                (lhProficiencies.includes("FOR") ? charaData.fortitude : 0),
                (lhProficiencies.includes("CHA") ? charaData.charisma : 0),
            ) : 0;
            
            return Attributes.BaseMagicDamage.getValueForScore(lhProfScore);
        }},
        "Base Magic Damage");

        statBoard.addFeature("DMG", "RHBaseDamage", ["rh"], {type: StatFeatureEnum.ADDEND, value: () => {
            const isRHActive = CharacterDataParser.isRHActive(charaData);
            const isDualWielding = CharacterDataParser.isDualWielding(charaData);

            if(!isRHActive || isDualWielding) {
                return 0;
            }

            const rhProficiencies = charaData.rh?.modifyingProficiencies ?? ["STR", "AGI", "INT"];

            const rhProfScore = isRHActive && !isDualWielding ?  Math.max(
                (rhProficiencies.includes("STR") ? charaData.strength : 0),
                (rhProficiencies.includes("AGI") ? charaData.agility : 0),
                (rhProficiencies.includes("INT") ? charaData.intelligence : 0),
                (rhProficiencies.includes("FOR") ? charaData.fortitude : 0),
                (rhProficiencies.includes("CHA") ? charaData.charisma : 0),
            ) : 0;
            
            return Attributes.BaseDamage.getValueForScore(rhProfScore);
        }},
        "Base Damage");

        statBoard.addFeature("MDMG", "RHBaseMagicDamage", ["rh"], {type: StatFeatureEnum.ADDEND, value: () => {
            const isRHActive = CharacterDataParser.isRHActive(charaData);
            const isDualWielding = CharacterDataParser.isDualWielding(charaData);

            if(!isRHActive || isDualWielding) {
                return 0;
            }

            const rhProficiencies = charaData.rh?.modifyingProficiencies ?? ["STR", "AGI", "INT"];

            const rhProfScore = isRHActive && !isDualWielding ?  Math.max(
                (rhProficiencies.includes("STR") ? charaData.strength : 0),
                (rhProficiencies.includes("AGI") ? charaData.agility : 0),
                (rhProficiencies.includes("INT") ? charaData.intelligence : 0),
                (rhProficiencies.includes("FOR") ? charaData.fortitude : 0),
                (rhProficiencies.includes("CHA") ? charaData.charisma : 0),
            ) : 0;
            
            return Attributes.BaseMagicDamage.getValueForScore(rhProfScore);
        }},
        "Base Magic Damage");

        // Weapon Dmg/MDmg
        const lhDmgResolved = charaData.lh?.dmg == null ? null : charaData.lh.dmg.toLocaleLowerCase().includes("d") ? DiceUtils.parseCompoundDiceString(charaData.lh.dmg) : Number.parseInt(charaData.lh.dmg);
        const lhMDmgResolved = charaData.lh?.mdmg == null ? null : charaData.lh.mdmg.toLocaleLowerCase().includes("d") ? DiceUtils.parseCompoundDiceString(charaData.lh.mdmg) : Number.parseInt(charaData.lh.mdmg);
        if(lhDmgResolved != null) {
            statBoard.addFeature("DMG", 
                lhWeaponAdjustValue, 
                ["lh"],
                typeof(lhDmgResolved) === "number" ? {
                    type: StatFeatureEnum.ADDEND, 
                    value: lhDmgResolved
                } : {
                    type: StatFeatureEnum.DICE, 
                    value: lhDmgResolved
                }, 
                charaData.lh?.title ?? "Left Hand Weapon"
            );
        }
        if(lhMDmgResolved != null) {
            statBoard.addFeature("MDMG", 
                lhWeaponAdjustValue, 
                ["lh"],
                typeof(lhMDmgResolved) === "number" ? {
                    type: StatFeatureEnum.ADDEND, 
                    value: lhMDmgResolved
                } : {
                    type: StatFeatureEnum.DICE, 
                    value: lhMDmgResolved
                }, 
                charaData.lh?.title ?? "Left Hand Weapon"
            );
        }

        const rhDmgResolved = charaData.rh?.dmg == null ? null : charaData.rh.dmg.toLocaleLowerCase().includes("d") ? DiceUtils.parseCompoundDiceString(charaData.rh.dmg) : Number.parseInt(charaData.rh.dmg);
        const rhMDmgResolved = charaData.rh?.mdmg == null ? null : charaData.rh.mdmg.toLocaleLowerCase().includes("d") ? DiceUtils.parseCompoundDiceString(charaData.rh.mdmg) : Number.parseInt(charaData.rh.mdmg);
        if(rhDmgResolved != null) {
            statBoard.addFeature("DMG", 
                rhWeaponAdjustValue, 
                ["rh"],
                typeof(rhDmgResolved) === "number" ? {
                    type: StatFeatureEnum.ADDEND, 
                    value: rhDmgResolved
                } : {
                    type: StatFeatureEnum.DICE, 
                    value: rhDmgResolved
                }, 
                charaData.rh?.title ?? "Right Hand Weapon"
            );
        }
        if(rhMDmgResolved != null) {
            statBoard.addFeature("MDMG", 
                rhWeaponAdjustValue, 
                ["rh"],
                typeof(rhMDmgResolved) === "number" ? {
                    type: StatFeatureEnum.ADDEND, 
                    value: rhMDmgResolved
                } : {
                    type: StatFeatureEnum.DICE, 
                    value: rhMDmgResolved
                }, 
                charaData.rh?.title ?? "Right Hand Weapon"
            );
        }
        console.log(charaData.rh?.dmg ?? "", rhDmgResolved);

        // Rng/MRng
        statBoard.addFeature("RNG", lhWeaponAdjustValue, ["lh"], {type: StatFeatureEnum.ADDEND, value: () => {
            return charaData.lh == null ? 1 : Number.parseInt(charaData.lh.rng ?? "0");
        }}, charaData.lh?.title ?? undefined);

        statBoard.addFeature("MRNG", lhWeaponAdjustValue, ["lh"], {type: StatFeatureEnum.ADDEND, value: () => {
            return charaData.lh == null ? 1 : Number.parseInt(charaData.lh.mrng ?? "0");
        }}, charaData.lh?.title ?? undefined);

        statBoard.addFeature("RNG", rhWeaponAdjustValue, ["rh"], {type: StatFeatureEnum.ADDEND, value: () => {
            return charaData.rh == null ? 1 : Number.parseInt(charaData.rh.rng ?? "0");
        }}, charaData.rh?.title ?? undefined);

        statBoard.addFeature("MRNG", rhWeaponAdjustValue, ["rh"], {type: StatFeatureEnum.ADDEND, value: () => {
            return charaData.rh == null ? 1 : Number.parseInt(charaData.rh.mrng ?? "0");
        }}, charaData.rh?.title ?? undefined);

        // Pow/MPow
        if(charaData.lh != null) {
            statBoard.addFeature("POW", lhWeaponAdjustValue, ["lh"], {type: StatFeatureEnum.ADDEND, value: () => {
                return Number.parseInt(charaData.lh?.pow ?? "0");
            }}, charaData.lh?.title ?? undefined);

            statBoard.addFeature("MPOW", lhWeaponAdjustValue, ["lh"], {type: StatFeatureEnum.ADDEND, value: () => {
                return Number.parseInt(charaData.lh?.mpow ?? "0");
            }}, charaData.lh?.title ?? undefined);
        }

        if(charaData.rh != null) {
            statBoard.addFeature("POW", rhWeaponAdjustValue, ["rh"], {type: StatFeatureEnum.ADDEND, value: () => {
                return Number.parseInt(charaData.rh?.pow ?? "0");
            }}, charaData.rh?.title ?? undefined);

            statBoard.addFeature("MPOW", rhWeaponAdjustValue, ["rh"], {type: StatFeatureEnum.ADDEND, value: () => {
                return Number.parseInt(charaData.rh?.mpow ?? "0");
            }}, charaData.rh?.title ?? undefined);
        }

        // Vitality
        statBoard.addFeature(
            "MPMAX",
            `Race`,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    if (charaData.race !== null) {
                        return Vitality.getMPValue(
                            VitalityRatingUtils.coerce(charaData.race.mpBase),
                            charaData.level
                        );
                    }
                    return 0;
                },
            },
            `Race${charaData.race ? `: ${charaData.race.name}` : ``}`
        );

        statBoard.addFeature(
            "MPMAX",
            IntChaAdjustValue.key,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return Attributes.MPAdjust.getValueForScore(
                        Math.floor(
                            (charaData.intelligence + charaData.charisma) / 2
                        )
                    );
                },
            },
            IntChaAdjustValue.tip
        );

        statBoard.addFeature(
            "MP",
            `Base`,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return Number.parseInt(
                        statBoard.getValueByStatKey("MPMAX")
                    );
                },
            },
            `Base Value`
        );

        statBoard.addFeature(
            "MP",
            `Exhaustion`,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return -charaData.exhaustion;
                },
            },
            `Exhaustion`
        );

        statBoard.addFeature(
            "MP",
            `MPAdjust`,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return charaData.mpAdjust;
                },
            },
            `MP Adjust`
        );

        statBoard.addFeature(
            "EPMAX",
            `Race`,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    if (charaData.race !== null) {
                        return Vitality.getEPValue(
                            VitalityRatingUtils.coerce(charaData.race.epBase),
                            charaData.level
                        );
                    }
                    return 0;
                },
            },
            `Race${charaData.race ? `: ${charaData.race.name}` : ``}`
        );

        statBoard.addFeature(
            "EPMAX",
            StrForAdjustValue.key,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return Attributes.EPAdjust.getValueForScore(
                        Math.floor(
                            (charaData.strength + charaData.fortitude) / 2
                        )
                    );
                },
            },
            StrForAdjustValue.tip
        );

        statBoard.addFeature(
            "EP",
            `Base`,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return Number.parseInt(
                        statBoard.getValueByStatKey("EPMAX")
                    );
                },
            },
            `Base Value`
        );

        statBoard.addFeature(
            "EP",
            `Exhaustion`,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return -charaData.exhaustion;
                },
            },
            `Exhaustion`
        );

        statBoard.addFeature(
            "EP",
            `EPAdjust`,
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return charaData.epAdjust;
                },
            },
            `EP Adjust`
        );

        // Equipment
        statBoard.addFeature(
            "DEF",
            "Torso",
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return Number.parseInt(charaData.torso?.def ?? "0");
                },
            },
            charaData.torso?.title
        );

        statBoard.addFeature(
            "MDEF",
            "Torso",
            null,
            {
                type: StatFeatureEnum.ADDEND,
                value: () => {
                    return Number.parseInt(charaData.torso?.mdef ?? "0");
                },
            },
            charaData.torso?.title
        );

        // Racial Adjustments
        if (charaData.race !== null) {
            charaData.race.raceModifiers.forEach((mod) => {
                statBoard.addFeature(
                    mod.key,
                    "Race",
                    null,
                    { type: StatFeatureEnum.ADDEND, value: mod.value },
                    `Race${charaData.race ? `: ${charaData.race.name}` : ``}`
                );
            });
        }

        // Traits
        if (charaData.traits.length > 0) {
            charaData.traits.forEach((trait) => {
                if (trait.active && trait.mods.length > 0) {
                    trait.mods.forEach((mod) => {
                        statBoard.addFeature(
                            mod.key,
                            `trait_${trait.id}`,
                            null,
                            {
                                type: StatFeatureEnum.ADDEND,
                                value: () => {
                                    return trait.active ? mod.value : 0;
                                },
                            },
                            `Trait: ${trait.title}`
                        );
                    });
                }
            });
        }
    }
}
